import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';

export interface Warehouse {
  id: string;
  code: string;
  name: string;
  address: string | null;
  isDefault: boolean;
  active: boolean;
}

export interface StockRow {
  productId: string;
  warehouseId: string;
  sku: string;
  name: string;
  unitCode: string;
  quantity: number;
  avgCost: number;
  value: number;
}

export interface KardexRow {
  id: string;
  type: string;
  quantity: number;
  unitCost: number | null;
  balanceAfter: number;
  reason: string | null;
  reference: string | null;
  lotCode: string | null;
  expiryDate: string | null;
  warehouseId: string;
  createdAt: string;
}

export type MovementType = 'IN' | 'OUT' | 'ADJUST' | 'TRANSFER';

export interface MovementInput {
  type: MovementType;
  productId: string;
  warehouseId: string;
  toWarehouseId?: string;
  quantity: number;
  unitCost?: number;
  reason?: string;
  reference?: string;
  lotCode?: string;
  expiryDate?: string;
}

function errMsg(e: unknown, fallback: string): string {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

export function useWarehouses() {
  return useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<Warehouse[]>('/inventory/warehouses')).data,
    retry: false,
  });
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; name: string; address?: string }) =>
      (await apiClient.post('/inventory/warehouses', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warehouses'] });
      toast.success('Almacen creado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo crear el almacen')),
  });
}

export function useStock(warehouseId: string | undefined) {
  return useQuery({
    queryKey: ['stock', warehouseId ?? 'all'],
    queryFn: async () =>
      (await apiClient.get<StockRow[]>('/inventory/stock', { params: warehouseId ? { warehouseId } : {} }))
        .data,
    retry: false,
  });
}

export function useCreateMovement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: MovementInput) => (await apiClient.post('/inventory/movements', input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stock'] });
      qc.invalidateQueries({ queryKey: ['kardex'] });
      toast.success('Movimiento registrado');
    },
    onError: (e: unknown) => toast.error(errMsg(e, 'No se pudo registrar el movimiento')),
  });
}

export function useKardex(productId: string | undefined, warehouseId?: string) {
  return useQuery({
    queryKey: ['kardex', productId, warehouseId ?? 'all'],
    queryFn: async () =>
      (
        await apiClient.get<KardexRow[]>('/inventory/kardex', {
          params: { productId, ...(warehouseId ? { warehouseId } : {}) },
        })
      ).data,
    enabled: !!productId,
    retry: false,
  });
}
